/**
 * AI-settings mirror of catalog-data.ts INTEGRATIONS_CATALOG (one group per
 * entry); ai-settings-groups.spec.ts pins the two in sync. Update on catalog change.
 */
import type {
  SettingAccess,
  SettingAgentEditability,
  SettingChangeGuidance,
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../settings-catalog/types";

export type IntegrationsHubSettingGroupId =
  | "integrations.adyen"
  | "integrations.aims360"
  | "integrations.amazon"
  | "integrations.amazon-shipping"
  | "integrations.apc"
  | "integrations.apparel-magic"
  | "integrations.aptos"
  | "integrations.asendia"
  | "integrations.attentive"
  | "integrations.australia-post"
  | "integrations.authorizenet"
  | "integrations.bigcommerce"
  | "integrations.blue-cherry"
  | "integrations.blue-yonder"
  | "integrations.brightpearl"
  | "integrations.bubblehouse"
  | "integrations.canada-post"
  | "integrations.canpar-express"
  | "integrations.chat-widget"
  | "integrations.chitchats"
  | "integrations.cin7-core"
  | "integrations.cin7-omni"
  | "integrations.cloudx"
  | "integrations.comment-sold"
  | "integrations.commerce-cloud"
  | "integrations.controlport"
  | "integrations.cordial"
  | "integrations.creditsyard"
  | "integrations.custom-return"
  | "integrations.deposco"
  | "integrations.dhl-ecommerce"
  | "integrations.dhl-express"
  | "integrations.disputely"
  | "integrations.dsco"
  | "integrations.easypost-carrier"
  | "integrations.ebay"
  | "integrations.ecom-express"
  | "integrations.ecwid"
  | "integrations.epost-global"
  | "integrations.eta-express"
  | "integrations.extensiv"
  | "integrations.facebook-messenger"
  | "integrations.faire"
  | "integrations.falcon"
  | "integrations.fedex"
  | "integrations.first-mile"
  | "integrations.flexport"
  | "integrations.globale"
  | "integrations.gls"
  | "integrations.google-ads"
  | "integrations.google-analytics"
  | "integrations.google-merchant-center"
  | "integrations.google-search-console"
  | "integrations.gorgias"
  | "integrations.growave"
  | "integrations.happy-returns"
  | "integrations.idf-shipping"
  | "integrations.indigo8"
  | "integrations.inmar"
  | "integrations.instagram"
  | "integrations.jitsu"
  | "integrations.judgeme"
  | "integrations.katana"
  | "integrations.klaviyo"
  | "integrations.kustomer"
  | "integrations.landmark-global"
  | "integrations.linnworks"
  | "integrations.liquidonate"
  | "integrations.loop"
  | "integrations.loop-subscriptions"
  | "integrations.loopz"
  | "integrations.loyalty-lion"
  | "integrations.luminous"
  | "integrations.masonhub"
  | "integrations.meta-ads"
  | "integrations.mintsoft"
  | "integrations.narvar"
  | "integrations.netsuite"
  | "integrations.odoo"
  | "integrations.okendo"
  | "integrations.ontrac"
  | "integrations.optty"
  | "integrations.orders-api"
  | "integrations.parent-easypost"
  | "integrations.passport-global"
  | "integrations.paypal"
  | "integrations.postscript"
  | "integrations.purolator"
  | "integrations.purple-dot"
  | "integrations.quickbooks-online"
  | "integrations.recharge"
  | "integrations.refundid"
  | "integrations.return-logic"
  | "integrations.richpanel"
  | "integrations.rise-ai"
  | "integrations.rithum"
  | "integrations.rivo"
  | "integrations.rokt"
  | "integrations.royal-mail"
  | "integrations.ryder"
  | "integrations.seko"
  | "integrations.seko-omniparcel"
  | "integrations.sendcloud"
  | "integrations.sfom"
  | "integrations.shipbob"
  | "integrations.shipedge"
  | "integrations.shiphero"
  | "integrations.shipmonk"
  | "integrations.shipstation"
  | "integrations.shopify"
  | "integrations.skio"
  | "integrations.smile"
  | "integrations.sms-voice"
  | "integrations.spee-dee"
  | "integrations.stallion-express"
  | "integrations.stamped"
  | "integrations.stay-ai"
  | "integrations.stripe"
  | "integrations.support-email"
  | "integrations.teamwork-commerce"
  | "integrations.temu"
  | "integrations.tiktok-ads"
  | "integrations.tiktok-shop"
  | "integrations.tiktok-support"
  | "integrations.trackstar"
  | "integrations.triple-whale"
  | "integrations.uniuni"
  | "integrations.ups"
  | "integrations.usps"
  | "integrations.veho"
  | "integrations.walmart"
  | "integrations.wayfair"
  | "integrations.woocommerce"
  | "integrations.yofi"
  | "integrations.yotpo"
  | "integrations.zonos";

const integrationsHubOwner: SettingOwner = {
  kind: "integration",
  product: "integrations",
};

const integrationConnectionRisk: SettingRisk = {
  level: "medium",
  reason:
    "Connecting or disconnecting integrations changes how data syncs between Redo and external systems.",
};

const neverAgentEditable: SettingAgentEditability = {
  mode: "never",
  reason: "Integrations are connected and managed on their integration page.",
};

const merchantIntegrationAccess: SettingAccess = {
  read: { minVisibility: "merchant" },
  write: {
    mode: "none",
    reason: "Connect and manage this integration on its integration page.",
  },
};

const redoManagedIntegrationAccess: SettingAccess = {
  read: { minVisibility: "merchant" },
  write: {
    mode: "none",
    reason:
      "This integration is set up by the Redo team. Reach out to your Redo contact to enable or change it.",
  },
};

const redoManagedChangeGuidance: SettingChangeGuidance = {
  summary:
    "Redo-managed integration that cannot be self-served. Tell the merchant to reach out to their Redo contact to set it up.",
};

export const IntegrationsHubSettingGroups = [
  {
    id: "integrations.adyen",
    title: "Adyen",
    description: "Adyen payments, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["adyen integration", "connect adyen"],
  },
  {
    id: "integrations.aims360",
    title: "Aims360",
    description: "Connect your Aims360 account to automatically sync returns",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/aims360" },
    synonyms: ["aims360 integration", "connect aims360"],
  },
  {
    id: "integrations.amazon",
    title: "Amazon",
    description: "Pull Amazon orders into Redo fulfillment",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/amazon" },
    synonyms: ["amazon integration", "connect amazon"],
  },
  {
    id: "integrations.amazon-shipping",
    title: "Amazon Shipping",
    description:
      "Connect your Amazon Shipping carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/amazon-shipping",
    },
    synonyms: ["amazon shipping integration", "connect amazon shipping"],
  },
  {
    id: "integrations.apc",
    title: "APC",
    description: "Connect your APC carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/apc" },
    synonyms: ["apc integration", "connect apc"],
  },
  {
    id: "integrations.apparel-magic",
    title: "ApparelMagic",
    description: "Connect your ApparelMagic account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/apparel-magic",
    },
    synonyms: [
      "apparelmagic integration",
      "connect apparelmagic",
      "apparel magic",
    ],
  },
  {
    id: "integrations.aptos",
    title: "Aptos",
    description: "Sync Aptos retail orders into Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/aptos" },
    synonyms: ["aptos integration", "connect aptos"],
  },
  {
    id: "integrations.asendia",
    title: "Asendia",
    description: "Connect your Asendia carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/asendia" },
    synonyms: ["asendia integration", "connect asendia"],
  },
  {
    id: "integrations.attentive",
    title: "Attentive",
    description: "Connect Attentive for SMS support and marketing",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/attentive" },
    synonyms: ["attentive integration", "connect attentive"],
  },
  {
    id: "integrations.australia-post",
    title: "Australia Post",
    description:
      "Connect your Australia Post carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/australia-post",
    },
    synonyms: ["australia post integration", "connect australia post"],
  },
  {
    id: "integrations.authorizenet",
    title: "Authorize.net",
    description: "Connect your Authorize.net account to process refunds",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/authorizenet",
    },
    synonyms: [
      "authorize.net integration",
      "connect authorize.net",
      "authorizenet",
    ],
  },
  {
    id: "integrations.bigcommerce",
    title: "BigCommerce",
    description: "BigCommerce order sync, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["bigcommerce integration", "connect bigcommerce"],
  },
  {
    id: "integrations.blue-cherry",
    title: "BlueCherry",
    description: "Connect your BlueCherry WMS account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/blue-cherry",
    },
    synonyms: ["bluecherry integration", "connect bluecherry", "blue cherry"],
  },
  {
    id: "integrations.blue-yonder",
    title: "Blue Yonder",
    description: "Connect your Blue Yonder WMS account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/blue-yonder",
    },
    synonyms: ["blue yonder integration", "connect blue yonder"],
  },
  {
    id: "integrations.brightpearl",
    title: "Brightpearl",
    description: "Connect your Brightpearl account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/brightpearl",
    },
    synonyms: ["brightpearl integration", "connect brightpearl"],
  },
  {
    id: "integrations.bubblehouse",
    title: "Bubblehouse",
    description:
      "Award loyalty points via Bubblehouse when a return is processed",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/bubblehouse",
    },
    synonyms: ["bubblehouse integration", "connect bubblehouse"],
  },
  {
    id: "integrations.canada-post",
    title: "Canada Post",
    description: "Connect your Canada Post carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/canada-post",
    },
    synonyms: ["canada post integration", "connect canada post"],
  },
  {
    id: "integrations.canpar-express",
    title: "Canpar Express",
    description:
      "Connect your Canpar Express carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/canpar-express",
    },
    synonyms: ["canpar express integration", "connect canpar express"],
  },
  {
    id: "integrations.chat-widget",
    title: "Custom website chat widget",
    description: "Chat with shoppers on your storefront through Redo support",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/chat-widget",
    },
    synonyms: [
      "custom website chat widget integration",
      "connect custom website chat widget",
      "chat widget",
    ],
  },
  {
    id: "integrations.chitchats",
    title: "ChitChats",
    description: "Connect your ChitChats carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/chitchats" },
    synonyms: ["chitchats integration", "connect chitchats"],
  },
  {
    id: "integrations.cin7-core",
    title: "Cin7 Core",
    description: "Sync orders and fulfillments with Cin7 Core",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/cin7-core" },
    synonyms: ["cin7 core integration", "connect cin7 core"],
  },
  {
    id: "integrations.cin7-omni",
    title: "Cin7 Omni",
    description:
      "Connect your Cin7 Omni account to sync returns and credit notes",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/cin7-omni" },
    synonyms: ["cin7 omni integration", "connect cin7 omni"],
  },
  {
    id: "integrations.cloudx",
    title: "CloudX/Bergen Logistics",
    description: "Connect your CloudX/Bergen Logistics account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/cloudx" },
    synonyms: [
      "cloudx/bergen logistics integration",
      "connect cloudx/bergen logistics",
      "cloudx",
    ],
  },
  {
    id: "integrations.comment-sold",
    title: "Comment Sold",
    description: "Connect your Comment Sold account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/comment-sold",
    },
    synonyms: ["comment sold integration", "connect comment sold"],
  },
  {
    id: "integrations.commerce-cloud",
    title: "Salesforce Commerce Cloud",
    description: "Commerce Cloud order sync, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: [
      "salesforce commerce cloud integration",
      "connect salesforce commerce cloud",
      "commerce cloud",
    ],
  },
  {
    id: "integrations.controlport",
    title: "ControlPort",
    description: "Connect your ControlPort warehouse to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/controlport",
    },
    synonyms: ["controlport integration", "connect controlport"],
  },
  {
    id: "integrations.cordial",
    title: "Cordial",
    description:
      "Connect your Cordial account to automatically sync customer events",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/cordial" },
    synonyms: ["cordial integration", "connect cordial"],
  },
  {
    id: "integrations.creditsyard",
    title: "CreditsYard",
    description: "Connect your CreditsYard account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/creditsyard",
    },
    synonyms: ["creditsyard integration", "connect creditsyard"],
  },
  {
    id: "integrations.custom-return",
    title: "Custom Return Integration",
    description: "Set up a custom return integration",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/custom-return",
    },
    synonyms: [
      "custom return integration integration",
      "connect custom return integration",
      "custom return",
    ],
  },
  {
    id: "integrations.deposco",
    title: "Deposco",
    description:
      "Connect your Deposco WMS account to automatically sync return RMAs",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/deposco" },
    synonyms: ["deposco integration", "connect deposco"],
  },
  {
    id: "integrations.dhl-ecommerce",
    title: "DHL eCommerce",
    description:
      "Connect your DHL eCommerce carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/dhl-ecommerce",
    },
    synonyms: ["dhl ecommerce integration", "connect dhl ecommerce"],
  },
  {
    id: "integrations.dhl-express",
    title: "DHL Express",
    description: "Connect your DHL Express carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/dhl-express",
    },
    synonyms: ["dhl express integration", "connect dhl express"],
  },
  {
    id: "integrations.disputely",
    title: "Disputely",
    description: "Disputely dispute automation, powering RESOLVE deflections",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "featureFlag.enabled",
          featureFlag: "dispute-resolve-ui",
        },
      ],
      unavailableBehavior: "hidden",
      reason: "This integration is gated behind a feature flag rollout.",
    },
    fullPageTarget: { type: "settings", path: "integrations/vendor/disputely" },
    synonyms: [
      "disputely integration",
      "connect disputely",
      "chargeback",
      "chargebacks",
      "chargeback disputes",
      "payment disputes",
      "fight chargebacks",
      "dispute automation",
    ],
  },
  {
    id: "integrations.dsco",
    title: "Dsco",
    description: "Connect your Dsco account to sync orders with Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/dsco" },
    synonyms: ["dsco integration", "connect dsco"],
  },
  {
    id: "integrations.easypost-carrier",
    title: "EasyPost Carrier Accounts",
    description: "Child EasyPost carrier accounts, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: [
      "easypost carrier accounts integration",
      "connect easypost carrier accounts",
      "easypost carrier",
    ],
  },
  {
    id: "integrations.ebay",
    title: "eBay",
    description: "Pull eBay orders into Redo fulfillment",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/ebay" },
    synonyms: ["ebay integration", "connect ebay"],
  },
  {
    id: "integrations.ecom-express",
    title: "Ecom Express",
    description:
      "Connect your Ecom Express carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/ecom-express",
    },
    synonyms: ["ecom express integration", "connect ecom express"],
  },
  {
    id: "integrations.ecwid",
    title: "Ecwid",
    description:
      "Create Ecwid orders automatically for returns, claims, or warranties.",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/ecwid" },
    synonyms: ["ecwid integration", "connect ecwid"],
  },
  {
    id: "integrations.epost-global",
    title: "ePost Global",
    description:
      "Connect your ePost Global carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/epost-global",
    },
    synonyms: ["epost global integration", "connect epost global"],
  },
  {
    id: "integrations.eta-express",
    title: "ETA Express",
    description: "Connect your ETA Express carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/eta-express",
    },
    synonyms: ["eta express integration", "connect eta express"],
  },
  {
    id: "integrations.extensiv",
    title: "Extensiv",
    description:
      "Connect your Extensiv (3PL Central) account to automatically sync return RMAs",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/extensiv" },
    synonyms: ["extensiv integration", "connect extensiv"],
  },
  {
    id: "integrations.facebook-messenger",
    title: "Facebook Messenger",
    description: "Answer Facebook DMs and comments from Redo support",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/facebook-messenger",
    },
    synonyms: ["facebook messenger integration", "connect facebook messenger"],
  },
  {
    id: "integrations.faire",
    title: "Faire",
    description: "Pull Faire wholesale orders into Redo fulfillment",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/faire" },
    synonyms: ["faire integration", "connect faire"],
  },
  {
    id: "integrations.falcon",
    title: "Redo Offer Network",
    description:
      "Show personalized offers on your tracking and thank-you pages",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "featureFlag.enabled",
          featureFlag: "offers-tracking-page-block",
        },
      ],
      unavailableBehavior: "hidden",
      reason: "This integration is gated behind a feature flag rollout.",
    },
    fullPageTarget: { type: "settings", path: "integrations/vendor/falcon" },
    synonyms: [
      "redo offer network integration",
      "connect redo offer network",
      "falcon",
    ],
  },
  {
    id: "integrations.fedex",
    title: "FedEx",
    description: "Connect your FedEx carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/fedex" },
    synonyms: ["fedex integration", "connect fedex"],
  },
  {
    id: "integrations.first-mile",
    title: "FirstMile",
    description: "Connect your FirstMile carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/first-mile",
    },
    synonyms: ["firstmile integration", "connect firstmile", "first mile"],
  },
  {
    id: "integrations.flexport",
    title: "Flexport",
    description: "Connect your Flexport account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/flexport" },
    synonyms: ["flexport integration", "connect flexport"],
  },
  {
    id: "integrations.globale",
    title: "Global-e",
    description: "Connect your Global-e account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/globale" },
    synonyms: ["global-e integration", "connect global-e", "globale"],
  },
  {
    id: "integrations.gls",
    title: "GLS",
    description: "Connect your GLS carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/gls" },
    synonyms: ["gls integration", "connect gls"],
  },
  {
    id: "integrations.google-ads",
    title: "Google Ads",
    description: "Connect your Redo segments to your Google Ads customer lists",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/google-ads",
    },
    synonyms: ["google ads integration", "connect google ads"],
  },
  {
    id: "integrations.google-analytics",
    title: "Google Analytics",
    description: "Connect Google Analytics to optimize for AI search",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/google-analytics",
    },
    synonyms: ["google analytics integration", "connect google analytics"],
  },
  {
    id: "integrations.google-merchant-center",
    title: "Google Merchant Center",
    description: "Google Merchant Center sync, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: [
      "google merchant center integration",
      "connect google merchant center",
    ],
  },
  {
    id: "integrations.google-search-console",
    title: "Google Search Console",
    description: "Connect Google Search Console to optimize for AI search",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/google-search-console",
    },
    synonyms: [
      "google search console integration",
      "connect google search console",
    ],
  },
  {
    id: "integrations.gorgias",
    title: "Gorgias",
    description:
      "Escalate support tickets to Gorgias and pull ticket history for dispute evidence",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/gorgias" },
    synonyms: ["gorgias integration", "connect gorgias"],
  },
  {
    id: "integrations.growave",
    title: "Growave",
    description: "Connect Growave to enable marketing automation triggers",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["growave integration", "connect growave"],
  },
  {
    id: "integrations.happy-returns",
    title: "Happy Returns",
    description: "Happy Returns dropoff, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["happy returns integration", "connect happy returns"],
  },
  {
    id: "integrations.idf-shipping",
    title: "IDF Shipping",
    description: "Connect your IDF Shipping account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/idf-shipping",
    },
    synonyms: ["idf shipping integration", "connect idf shipping"],
  },
  {
    id: "integrations.indigo8",
    title: "Indigo8",
    description: "Connect your Indigo8 account to sync returns automatically.",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/indigo8" },
    synonyms: ["indigo8 integration", "connect indigo8"],
  },
  {
    id: "integrations.inmar",
    title: "Inmar",
    description: "Connect your Inmar account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/inmar" },
    synonyms: ["inmar integration", "connect inmar"],
  },
  {
    id: "integrations.instagram",
    title: "Instagram",
    description: "Answer Instagram DMs and comments from Redo support",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/instagram" },
    synonyms: ["instagram integration", "connect instagram"],
  },
  {
    id: "integrations.jitsu",
    title: "Jitsu",
    description: "Connect your Jitsu carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/jitsu" },
    synonyms: ["jitsu integration", "connect jitsu"],
  },
  {
    id: "integrations.judgeme",
    title: "Judge.me",
    description: "Connect Judge.me to sync product reviews with Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/judgeme" },
    synonyms: ["judge.me integration", "connect judge.me", "judgeme"],
  },
  {
    id: "integrations.katana",
    title: "Katana",
    description: "Connect your Katana account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/katana" },
    synonyms: ["katana integration", "connect katana"],
  },
  {
    id: "integrations.klaviyo",
    title: "Klaviyo",
    description:
      "Connect your Klaviyo account to automatically sync customer events",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/klaviyo" },
    synonyms: ["klaviyo integration", "connect klaviyo"],
  },
  {
    id: "integrations.kustomer",
    title: "Kustomer",
    description: "Kustomer helpdesk sync, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["kustomer integration", "connect kustomer"],
  },
  {
    id: "integrations.landmark-global",
    title: "Landmark Global",
    description: "Cross-border fulfillment through Landmark Global",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/landmark-global",
    },
    synonyms: ["landmark global integration", "connect landmark global"],
  },
  {
    id: "integrations.linnworks",
    title: "Linnworks",
    description: "Connect Linnworks directly to Redo for order fulfillment",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/linnworks" },
    synonyms: ["linnworks integration", "connect linnworks"],
  },
  {
    id: "integrations.liquidonate",
    title: "LiquiDonate",
    description:
      "Route eligible returns to a nearby nonprofit through LiquiDonate",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/liquidonate",
    },
    synonyms: ["liquidonate integration", "connect liquidonate"],
  },
  {
    id: "integrations.loop",
    title: "Loop",
    description: "Connect your Loop returns account to Redo",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["loop integration", "connect loop"],
  },
  {
    id: "integrations.loop-subscriptions",
    title: "Loop Subscriptions",
    description: "Use Loop subscription history as Redo dispute evidence",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/loop-subscriptions",
    },
    synonyms: [
      "loop subscriptions integration",
      "connect loop subscriptions",
      "loop subscription evidence",
    ],
  },
  {
    id: "integrations.loopz",
    title: "Loopz",
    description: "Connect your Loopz account to issue gift cards on returns",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/loopz" },
    synonyms: ["loopz integration", "connect loopz"],
  },
  {
    id: "integrations.loyalty-lion",
    title: "LoyaltyLion",
    description: "Connect LoyaltyLion to sync loyalty points with Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/loyalty-lion",
    },
    synonyms: [
      "loyaltylion integration",
      "connect loyaltylion",
      "loyalty lion",
    ],
  },
  {
    id: "integrations.luminous",
    title: "Luminous",
    description: "Connect your Luminous account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/luminous" },
    synonyms: ["luminous integration", "connect luminous"],
  },
  {
    id: "integrations.masonhub",
    title: "MasonHub",
    description: "Connect your MasonHub account to sync returns automatically.",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/masonhub" },
    synonyms: ["masonhub integration", "connect masonhub"],
  },
  {
    id: "integrations.meta-ads",
    title: "Meta Ads & Lead Forms",
    description:
      "Sync Redo segments to Meta custom audiences and capture leads from Meta forms",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/meta-ads" },
    synonyms: [
      "meta ads integration",
      "meta lead forms",
      "meta lead ads",
      "connect meta ads",
    ],
  },
  {
    id: "integrations.mintsoft",
    title: "Mintsoft",
    description: "Connect your Mintsoft WMS account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/mintsoft" },
    synonyms: ["mintsoft integration", "connect mintsoft"],
  },
  {
    id: "integrations.narvar",
    title: "Narvar",
    description: "Connect your Narvar returns account to Redo",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["narvar integration", "connect narvar"],
  },
  {
    id: "integrations.netsuite",
    title: "NetSuite",
    description: "Connect your NetSuite account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/netsuite" },
    synonyms: ["netsuite integration", "connect netsuite"],
  },
  {
    id: "integrations.odoo",
    title: "Odoo",
    description:
      "Connect your Odoo account to create credit notes for returns automatically.",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/odoo" },
    synonyms: ["odoo integration", "connect odoo"],
  },
  {
    id: "integrations.okendo",
    title: "Okendo",
    description: "Connect Okendo to sync reviews and loyalty with Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "featureFlag.enabled",
          featureFlag: "okendo-marketing-integration",
        },
      ],
      unavailableBehavior: "hidden",
      reason: "This integration is gated behind a feature flag rollout.",
    },
    fullPageTarget: { type: "settings", path: "integrations/vendor/okendo" },
    synonyms: ["okendo integration", "connect okendo"],
  },
  {
    id: "integrations.ontrac",
    title: "OnTrac",
    description: "Connect your OnTrac carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/ontrac" },
    synonyms: ["ontrac integration", "connect ontrac"],
  },
  {
    id: "integrations.optty",
    title: "Optty",
    description: "Optty payments, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["optty integration", "connect optty"],
  },
  {
    id: "integrations.orders-api",
    title: "Orders API",
    description: "Send order events to Redo marketing via the Orders API",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/orders-api",
    },
    synonyms: ["orders api integration", "connect orders api"],
  },
  {
    id: "integrations.parent-easypost",
    title: "EasyPost",
    description: "Connect your EasyPost account for outbound labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/parent-easypost",
    },
    synonyms: ["easypost integration", "connect easypost", "parent easypost"],
  },
  {
    id: "integrations.passport-global",
    title: "Passport Global",
    description:
      "Connect your Passport Global carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/passport-global",
    },
    synonyms: ["passport global integration", "connect passport global"],
  },
  {
    id: "integrations.paypal",
    title: "PayPal",
    description: "Connect PayPal to manage payment disputes in Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/paypal" },
    synonyms: ["paypal integration", "connect paypal"],
  },
  {
    id: "integrations.postscript",
    title: "Postscript",
    description: "Connect Postscript for SMS support and marketing",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/postscript",
    },
    synonyms: ["postscript integration", "connect postscript"],
  },
  {
    id: "integrations.purolator",
    title: "Purolator",
    description: "Connect your Purolator carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/purolator" },
    synonyms: ["purolator integration", "connect purolator"],
  },
  {
    id: "integrations.purple-dot",
    title: "Purple Dot",
    description: "Connect your Purple Dot account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/purple-dot",
    },
    synonyms: ["purple dot integration", "connect purple dot"],
  },
  {
    id: "integrations.quickbooks-online",
    title: "QuickBooks Online",
    description: "Connect QuickBooks Online to sync inventory and accounting",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "featureFlag.enabled",
          featureFlag: "ims-quickbooks",
        },
      ],
      unavailableBehavior: "hidden",
      reason: "This integration is gated behind a feature flag rollout.",
    },
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/quickbooks-online",
    },
    synonyms: ["quickbooks online integration", "connect quickbooks online"],
  },
  {
    id: "integrations.recharge",
    title: "Recharge",
    description: "Sync Recharge subscriptions with Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/recharge" },
    synonyms: ["recharge integration", "connect recharge"],
  },
  {
    id: "integrations.refundid",
    title: "Refundid",
    description: "Connect your Refundid account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/refundid" },
    synonyms: ["refundid integration", "connect refundid"],
  },
  {
    id: "integrations.return-logic",
    title: "ReturnLogic",
    description: "Connect your ReturnLogic returns account to Redo",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: [
      "returnlogic integration",
      "connect returnlogic",
      "return logic",
    ],
  },
  {
    id: "integrations.richpanel",
    title: "Richpanel",
    description: "Escalate support conversations to Richpanel over SMS",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/richpanel" },
    synonyms: ["richpanel integration", "connect richpanel"],
  },
  {
    id: "integrations.rise-ai",
    title: "Rise.ai",
    description: "Connect your Rise.ai account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/rise-ai" },
    synonyms: ["rise.ai integration", "connect rise.ai", "rise ai"],
  },
  {
    id: "integrations.rithum",
    title: "Rithum",
    description: "Sync Rithum (CommerceHub) dropship orders with Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/rithum" },
    synonyms: ["rithum integration", "connect rithum"],
  },
  {
    id: "integrations.rivo",
    title: "Rivo",
    description: "Connect your Rivo account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/rivo" },
    synonyms: ["rivo integration", "connect rivo"],
  },
  {
    id: "integrations.rokt",
    title: "Rokt",
    description: "Show personalized Rokt offers on your tracking page",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "featureFlag.enabled",
          featureFlag: "offers-tracking-page-block",
        },
      ],
      unavailableBehavior: "hidden",
      reason: "This integration is gated behind a feature flag rollout.",
    },
    fullPageTarget: { type: "settings", path: "integrations/vendor/rokt" },
    synonyms: ["rokt integration", "connect rokt"],
  },
  {
    id: "integrations.royal-mail",
    title: "Royal Mail",
    description: "Connect your Royal Mail carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/royal-mail",
    },
    synonyms: ["royal mail integration", "connect royal mail"],
  },
  {
    id: "integrations.ryder",
    title: "RyderShip",
    description: "Connect your RyderShip account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/ryder" },
    synonyms: ["rydership integration", "connect rydership", "ryder"],
  },
  {
    id: "integrations.seko",
    title: "Seko",
    description: "Connect your Seko account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/seko" },
    synonyms: ["seko integration", "connect seko"],
  },
  {
    id: "integrations.seko-omniparcel",
    title: "OmniParcel - Seko",
    description:
      "Connect your OmniParcel - Seko carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/seko-omniparcel",
    },
    synonyms: [
      "omniparcel - seko integration",
      "connect omniparcel - seko",
      "seko omniparcel",
    ],
  },
  {
    id: "integrations.sendcloud",
    title: "Sendcloud",
    description: "Connect your Sendcloud carrier account for return labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/sendcloud" },
    synonyms: ["sendcloud integration", "connect sendcloud"],
  },
  {
    id: "integrations.sfom",
    title: "SFOM",
    description: "SFOM returns sync, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["sfom integration", "connect sfom"],
  },
  {
    id: "integrations.shipbob",
    title: "ShipBob",
    description: "Connect your ShipBob account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/shipbob" },
    synonyms: ["shipbob integration", "connect shipbob"],
  },
  {
    id: "integrations.shipedge",
    title: "ShipEdge",
    description: "Connect your ShipEdge account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/shipedge" },
    synonyms: ["shipedge integration", "connect shipedge"],
  },
  {
    id: "integrations.shiphero",
    title: "ShipHero",
    description: "Connect your ShipHero account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/shiphero" },
    synonyms: ["shiphero integration", "connect shiphero"],
  },
  {
    id: "integrations.shipmonk",
    title: "ShipMonk",
    description: "Connect your ShipMonk account to sync returns automatically.",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/shipmonk" },
    synonyms: ["shipmonk integration", "connect shipmonk"],
  },
  {
    id: "integrations.shipstation",
    title: "ShipStation",
    description: "Sync orders and shipments with ShipStation",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/shipstation",
    },
    synonyms: ["shipstation integration", "connect shipstation"],
  },
  {
    id: "integrations.shopify",
    title: "Shopify",
    description: "Your Shopify store powers Redo across every product",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/shopify" },
    synonyms: ["shopify integration", "connect shopify"],
  },
  {
    id: "integrations.skio",
    title: "Skio",
    description: "Sync Skio subscriptions with Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/skio" },
    synonyms: ["skio integration", "connect skio"],
  },
  {
    id: "integrations.smile",
    title: "Smile.io",
    description: "Connect Smile.io to surface loyalty points in Redo messaging",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/smile" },
    synonyms: [
      "smile integration",
      "smile.io integration",
      "connect smile.io",
      "smile loyalty",
      "smile.io loyalty",
    ],
  },
  {
    id: "integrations.sms-voice",
    title: "SMS/Voice",
    description: "Support shoppers over text and phone calls in Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/sms-voice" },
    synonyms: ["sms/voice integration", "connect sms/voice", "sms voice"],
  },
  {
    id: "integrations.spee-dee",
    title: "SpeeDee",
    description: "Connect your SpeeDee carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/spee-dee" },
    synonyms: ["speedee integration", "connect speedee", "spee dee"],
  },
  {
    id: "integrations.stallion-express",
    title: "Stallion Express",
    description:
      "Connect your Stallion Express carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/stallion-express",
    },
    synonyms: ["stallion express integration", "connect stallion express"],
  },
  {
    id: "integrations.stamped",
    title: "Stamped",
    description: "Connect Stamped to show product reviews in Redo checkout",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/stamped" },
    synonyms: ["stamped integration", "connect stamped", "stamped.io"],
  },
  {
    id: "integrations.stay-ai",
    title: "Stay AI",
    description: "Sync Stay AI subscriptions with Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/stay-ai" },
    synonyms: ["stay ai integration", "connect stay ai"],
  },
  {
    id: "integrations.stripe",
    title: "Stripe",
    description: "Connect Stripe to manage payment disputes in Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/stripe" },
    synonyms: ["stripe integration", "connect stripe"],
  },
  {
    id: "integrations.support-email",
    title: "Email",
    description: "Connect Gmail or Outlook to answer support email in Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/support-email",
    },
    synonyms: ["email integration", "connect email", "support email"],
  },
  {
    id: "integrations.teamwork-commerce",
    title: "Teamwork Commerce",
    description: "Connect your Teamwork Commerce account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/teamwork-commerce",
    },
    synonyms: ["teamwork commerce integration", "connect teamwork commerce"],
  },
  {
    id: "integrations.temu",
    title: "Temu",
    description: "Pull Temu orders into Redo fulfillment",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "featureFlag.enabled",
          featureFlag: "temu-integration",
        },
      ],
      unavailableBehavior: "hidden",
      reason: "This integration is gated behind a feature flag rollout.",
    },
    fullPageTarget: { type: "settings", path: "integrations/vendor/temu" },
    synonyms: ["temu integration", "connect temu"],
  },
  {
    id: "integrations.tiktok-ads",
    title: "TikTok Ads",
    description: "Sync Redo segments to TikTok Ads custom audiences",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "featureFlag.enabled",
          featureFlag: "tiktok-ads-integration",
        },
      ],
      unavailableBehavior: "hidden",
      reason: "This integration is gated behind a feature flag rollout.",
    },
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/tiktok-ads",
    },
    synonyms: ["tiktok ads integration", "connect tiktok ads"],
  },
  {
    id: "integrations.tiktok-shop",
    title: "TikTok Shop",
    description: "Connect your TikTok Shop to manage returns through Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/tiktok-shop",
    },
    synonyms: ["tiktok shop integration", "connect tiktok shop"],
  },
  {
    id: "integrations.tiktok-support",
    title: "TikTok Messaging",
    description: "Answer TikTok messages from Redo support",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/tiktok-support",
    },
    synonyms: [
      "tiktok messaging integration",
      "connect tiktok messaging",
      "tiktok support",
    ],
  },
  {
    id: "integrations.trackstar",
    title: "TrackStar",
    description: "Connect your TrackStar account to Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/trackstar" },
    synonyms: ["trackstar integration", "connect trackstar"],
  },
  {
    id: "integrations.triple-whale",
    title: "Triple Whale",
    description: "Send Redo data to Triple Whale analytics",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/triple-whale",
    },
    synonyms: ["triple whale integration", "connect triple whale"],
  },
  {
    id: "integrations.uniuni",
    title: "UniUni",
    description: "Connect your UniUni carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/uniuni" },
    synonyms: ["uniuni integration", "connect uniuni"],
  },
  {
    id: "integrations.ups",
    title: "UPS",
    description: "Connect your UPS carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/ups" },
    synonyms: ["ups integration", "connect ups"],
  },
  {
    id: "integrations.usps",
    title: "USPS",
    description: "Connect your USPS carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/usps" },
    synonyms: ["usps integration", "connect usps"],
  },
  {
    id: "integrations.veho",
    title: "Veho",
    description: "Connect your Veho carrier account for shipping labels",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/veho" },
    synonyms: ["veho integration", "connect veho"],
  },
  {
    id: "integrations.walmart",
    title: "Walmart",
    description: "Pull Walmart marketplace orders into Redo fulfillment",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/walmart" },
    synonyms: ["walmart integration", "connect walmart"],
  },
  {
    id: "integrations.wayfair",
    title: "Wayfair",
    description: "Pull Wayfair orders into Redo fulfillment",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/wayfair" },
    synonyms: ["wayfair integration", "connect wayfair"],
  },
  {
    id: "integrations.woocommerce",
    title: "WooCommerce",
    description: "Pull WooCommerce orders into Redo fulfillment",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: {
      type: "settings",
      path: "integrations/vendor/woocommerce",
    },
    synonyms: ["woocommerce integration", "connect woocommerce"],
  },
  {
    id: "integrations.yofi",
    title: "Yofi",
    description: "Yofi fraud and bot detection, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["yofi integration", "connect yofi"],
  },
  {
    id: "integrations.yotpo",
    title: "Yotpo",
    description: "Connect Yotpo to sync reviews and loyalty with Redo",
    productArea: "integrations",
    access: merchantIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    fullPageTarget: { type: "settings", path: "integrations/vendor/yotpo" },
    synonyms: ["yotpo integration", "connect yotpo"],
  },
  {
    id: "integrations.zonos",
    title: "Zonos",
    description: "Zonos cross-border duties, set up by the Redo team",
    productArea: "integrations",
    access: redoManagedIntegrationAccess,
    owner: integrationsHubOwner,
    risk: integrationConnectionRisk,
    agentEditability: neverAgentEditable,
    changeGuidance: redoManagedChangeGuidance,
    synonyms: ["zonos integration", "connect zonos"],
  },
] as const satisfies readonly SettingGroupDefinition<IntegrationsHubSettingGroupId>[];
